// src/pages/notifications/TemplateEditPage.tsx
// Template edit — same create-dialog shape as TemplateCreatePage, prefilled
// from the existing template. Route-hosted (kept for TemplateDetailPage's
// "Edit" link) — the dialog opens over the detail page on mount and closing
// returns to it.

import { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useNotificationsTemplate, useI18n } from '@react/hooks';
import { FormDialog, TitleInput, AutoGrowTextarea } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { Select } from '@/components/Select';
import { validateTemplateForm, formatChannelType, formatCategory } from '@/features/notifications/logic';
import { TemplateVariablesEditor } from '@/features/notifications/TemplateVariablesEditor';
import { NotificationChannelTypeValues } from '@core/types/notifications/NotificationChannelType';
import { NotificationCategoryValues } from '@core/types/notifications/NotificationCategory';
import type { NotificationTemplate } from '@core/types/notifications/NotificationTemplate';
import type { TemplateFormState } from '@/features/notifications/types';

export const TemplateEditPage = observer(() => {
  const store = useNotificationsTemplate();
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState<TemplateFormState>({
    name: '', description: '', channelType: 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED', language: 'en',
    eventType: '', category: 'NOTIFICATION_CATEGORY_UNSPECIFIED', subjectTemplate: '', bodyTemplate: '',
    requiredVariables: '', isActive: true, isDefault: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [loaded, setLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { if (id) store.GetTemplate(id); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tpl = store.currentItem as NotificationTemplate | null;
    if (tpl && !loaded) {
      setForm({
        name: tpl.name ?? '', description: tpl.description ?? '', channelType: tpl.channel_type ?? 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED',
        language: tpl.language ?? 'en', eventType: tpl.event_type ?? '', category: tpl.category ?? 'NOTIFICATION_CATEGORY_UNSPECIFIED',
        subjectTemplate: tpl.subject_template ?? '', bodyTemplate: tpl.body_template ?? '',
        requiredVariables: (tpl.required_variables ?? []).join(', '), isActive: tpl.is_active ?? true, isDefault: tpl.is_default ?? false,
      });
      setLoaded(true);
    }
  }, [store.currentItem, loaded]);

  const close = () => navigate(`/notifications/templates/${id}`);

  const requiredVars = form.requiredVariables.split(',').map((s) => s.trim()).filter(Boolean);
  const setRequiredVars = (vars: string[]) => setForm((p) => ({ ...p, requiredVariables: vars.join(', ') }));

  const submit = async () => {
    const validation = validateTemplateForm(form);
    if (Object.keys(validation).length > 0) {
      setErrors(Object.fromEntries(Object.entries(validation).map(([k, v]) => [k, t(v)])));
      return;
    }
    setIsSubmitting(true);
    try {
      const current = store.currentItem as NotificationTemplate;
      await store.UpdateTemplate(id ?? '', {
        ...current,
        name: form.name.trim(), description: form.description.trim(), channel_type: form.channelType,
        language: form.language, event_type: form.eventType, category: form.category,
        subject_template: form.subjectTemplate, body_template: form.bodyTemplate,
        required_variables: requiredVars,
        is_active: form.isActive, is_default: form.isDefault,
      } as never);
      close();
    } catch (err) {
      setFormError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (store.isLoadingItem || !loaded) return <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>;

  return (
    <FormDialog
      open
      onClose={close}
      onSubmit={submit}
      submitLabel={t('common.saveChanges')}
      canSubmit={!!form.name.trim() && !!form.subjectTemplate.trim() && !!form.bodyTemplate.trim()}
      isSubmitting={isSubmitting}
      dirty
      width="lg"
      aria-label={t('notifications.editTemplate')}
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        <TitleInput
          value={form.name}
          onChange={(v) => setForm((p) => ({ ...p, name: v }))}
          placeholder={t('notifications.templateVariables.namePlaceholder')}
          initialFocus
          onEnter={() => document.getElementById('template-edit-description')?.focus()}
          aria-label={t('common.name')}
        />
        {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
        <AutoGrowTextarea
          id="template-edit-description"
          value={form.description}
          onChange={(v) => setForm((p) => ({ ...p, description: v }))}
          placeholder={t('notifications.templateVariables.descriptionPlaceholder')}
          minRows={1}
        />

        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={form.channelType}
            onChange={(e) => setForm((p) => ({ ...p, channelType: e.target.value }))}
            fullWidth={false}
            className="w-44"
            aria-label={t('notifications.channelType')}
          >
            {NotificationChannelTypeValues.filter((v) => v !== 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED').map((s) => {
              const ct = formatChannelType(s);
              return <option key={s} value={s}>{t(ct.labelKey)}</option>;
            })}
          </Select>
          <Select
            value={form.category}
            onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
            fullWidth={false}
            className="w-40"
            aria-label={t('notifications.category')}
          >
            {NotificationCategoryValues.map((s) => {
              const cat = formatCategory(s);
              return <option key={s} value={s}>{t(cat.labelKey)}</option>;
            })}
          </Select>
          <input
            value={form.language}
            onChange={(e) => setForm((p) => ({ ...p, language: e.target.value }))}
            placeholder={t('notifications.language')}
            aria-label={t('notifications.language')}
            autoCapitalize="off"
            className="w-20 rounded-md border border-input bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <input
            value={form.eventType}
            onChange={(e) => setForm((p) => ({ ...p, eventType: e.target.value }))}
            placeholder={t('notifications.templateVariables.eventTypePlaceholder')}
            aria-label={t('notifications.eventType')}
            autoCapitalize="off"
            className="w-44 rounded-md border border-input bg-background px-2 py-1.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        {errors.channelType && <p className="text-sm text-destructive">{errors.channelType}</p>}
        {errors.language && <p className="text-sm text-destructive">{errors.language}</p>}

        <div className="flex flex-wrap items-center gap-4 border-t border-border pt-2">
          <ToggleSwitch label={t('notifications.active')} checked={form.isActive} onChange={(v) => setForm((p) => ({ ...p, isActive: v }))} />
          <ToggleSwitch label={t('notifications.default')} checked={form.isDefault} onChange={(v) => setForm((p) => ({ ...p, isDefault: v }))} />
        </div>

        <div className="space-y-2 border-t border-border pt-2">
          <AutoGrowTextarea
            value={form.subjectTemplate}
            onChange={(v) => setForm((p) => ({ ...p, subjectTemplate: v }))}
            placeholder={t('notifications.templateVariables.subjectPlaceholder')}
            minRows={1}
            className="font-mono text-sm"
          />
          {errors.subjectTemplate && <p className="text-sm text-destructive">{errors.subjectTemplate}</p>}
          <AutoGrowTextarea
            ref={bodyRef}
            value={form.bodyTemplate}
            onChange={(v) => setForm((p) => ({ ...p, bodyTemplate: v }))}
            placeholder={t('notifications.templateVariables.bodyPlaceholder')}
            minRows={6}
            className="font-mono text-sm"
          />
          {errors.bodyTemplate && <p className="text-sm text-destructive">{errors.bodyTemplate}</p>}
          <TemplateVariablesEditor
            bodyRef={bodyRef}
            subjectTemplate={form.subjectTemplate}
            bodyTemplate={form.bodyTemplate}
            onBodyChange={(v) => setForm((p) => ({ ...p, bodyTemplate: v }))}
            requiredVariables={requiredVars}
            onRequiredVariablesChange={setRequiredVars}
          />
        </div>

        {formError != null && (
          <p className="text-sm text-destructive">{formError instanceof Error ? formError.message : t('notifications.saveError')}</p>
        )}
      </div>
    </FormDialog>
  );
});

export default TemplateEditPage;
